import { Button } from "@component/button";
import { useUserProfileContext } from "@context/user_profile";
import { useNavigate } from "react-router-dom";
import { LogOutIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ourteamLogo from "@asset/ourteam_logo.png";
import { removeCookie } from "@util/cookie";
import { getPostAuthLandingPath } from "@util/auth-redirect";

interface Props {
  customLogo?: string;
  hideNavbarMenu?: boolean
  isHideLoginButton?: boolean;
}

export function Navbar(props: Props) {
  const navigate = useNavigate();
  const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const onLoginClick = () => {
    navigate("/");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (isLoadingUserInfo) return <></>;

  return (
    <header className="border-b border-gray-200 bg-white" ref={dropdownRef}>
      <div className="container mx-auto px-4 py-2 h-14 flex items-center justify-between">
        <div className="flex items-center">
          <div
            className="text-xl font-bold cursor-pointer"
            onClick={() => {
              // Authenticated users go to their role-aware landing path
              // (e.g. /dashboard for employers, /interview for demo
              // job_seekers). Anonymous visitors fall back to the
              // login page at "/".
              window.location.href = currentUserInfo
                ? getPostAuthLandingPath(currentUserInfo.role)
                : "/";
            }}
          >
            {props.customLogo ? (
              // A custom logo is a remote asset that can 404 (a workspace that
              // never uploaded one, a stale path). Fall back to the ourteam mark
              // rather than leaving a broken-image icon in the header — same
              // guard jobseeker-first-time-register.tsx already uses.
              <img
                src={props.customLogo}
                alt="logo"
                width="40px"
                className="rounded-full"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = ourteamLogo;
                  e.currentTarget.width = 80;
                  e.currentTarget.className = "";
                }}
              />
            ) : (
              <img src={ourteamLogo} alt="ourteam" width="80px" />
            )}
          </div>
        </div>
        {!props.hideNavbarMenu && (
          <div className="flex gap-4 items-center">
            {!currentUserInfo ? (
              <div className="flex items-center space-x-4">

                <Button
                  variant="ghost"
                  onClick={() => {
                    window.location.href = "https://ourteam.ai/faq"
                  }}
                >
                  Questions?
                </Button>
                {!props.isHideLoginButton && (
                  <Button
                    variant="outline"
                    onClick={onLoginClick}
                  >
                    Log in
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex flex-row items-center space-x-2">
                <Button
                  variant="outline"
                  className="rounded-full flex items-center gap-2"
                    onClick={() => navigate(getPostAuthLandingPath(currentUserInfo.role))}
                >
                  Go to dashboard
                </Button>
                <div className="relative">
                  <div className="h-10 w-10 rounded-full overflow-hidden cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
                    <img
                      src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${currentUserInfo?.profile_picture_path || ""}`}
                      onError={(e) => {
                        e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/default_avatar.png`;
                      }}
                    />
                  </div>
                  {isOpen && (
                    <div className="absolute right-0 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 transition-all duration-200 ease-in-out">
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Signed in as
                        </p>
                        <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {currentUserInfo.email}
                        </p>
                      </div>
                      <div className="py-1">
                        <button
                          className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => {
                            setIsOpen(false);
                            removeCookie("authToken");
                            sessionStorage.removeItem("cv_uuid");
                            removeCookie("__apply_job_guest__")
                            window.location.href = "/";
                          }}
                        >
                          <LogOutIcon size={16} className="mr-2" />
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
